import {Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {Logger} from 'src/util/logger';
import {Address} from '../../gen/model/address';
import {AuthenticationChallenge} from '../../gen/model/authenticationChallenge';
import {UserForRegisterUser} from '../../gen/model/userForRegisterUser';
import ResponseUtil from '../../util/responseUtil';
import {AppConstants} from '../AppConstants';
import {CountriesService} from '../countries-service';
import {FormInputComponent} from '../form-input/form-input.component';
import {LoginBaseComponent} from '../login-base.component';
import {ParamsService} from '../params.service';
import {AlertHandler} from '../no-concurrent-alerts-handler';
import {cardFooterFeedbackTransition} from '../animations';
import LocationUtil, {getAppBaseHref} from '../../util/locationUtil';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';
import {ConfigurationService} from '../ui-configuration/configuration-service';
import {AppStateService, ParamsForRouteNav} from '../app-state.service';
import {marker as _} from '@biesbjerg/ngx-translate-extract-marker';
import {ProcessingIndicatorService} from '../processing-indicator.service';
import {AcceptAgreementsChallenge, AgreementAcceptance} from '../../gen';
import {
  AcceptAgreementChallengeWithReceivedTime,
  flattenChallenges, selectAgreements,
} from '../../util/AgreementUtils';
import * as stream from 'stream';
import {StandaloneComponent} from '../standalone.component';

const cloneDeep = require('lodash.clonedeep');

interface Item {
  value: string;
  label: string;
}
@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    cardFooterFeedbackTransition,
  ],
})
export class RegisterComponent extends LoginBaseComponent implements OnInit, OnDestroy {
  @ViewChild('credentials') credentialsComp;
  isAvailable = true;
  availability: string[];
  fields;
  loginFields;
  registerForm: FormGroup;
  email: string;
  emailAsReadOnly = false;
  invitationAuthorization: string|undefined = undefined;
  countryList: Item[];
  processing = false;

  agreements: AcceptAgreementChallengeWithReceivedTime[];
  private step: string;
  errorBody: any;
  private genericError = false;
  invitationError = false;

  consentFailed = false;

  constructor(
    private appStateService: AppStateService,
    private countries: CountriesService,
    route: ActivatedRoute,
    router: Router,
    private logger: Logger,
    private alertHandler: AlertHandler,
    private paramsService: ParamsService,
    private translate: TranslateService,
    private processingService: ProcessingIndicatorService,
    hostElement: ElementRef,
    configuration: ConfigurationService
  ) {
    super(route, router, hostElement, configuration);
    this.fields = cloneDeep(this.configuration.getProperties().registrationFields);
    this.loginFields = this.configuration.getProperties().profileLoginFields;
    this.credsReady = this.credsReady.bind(this);
    this.availability = this.configuration.getProperties().registrationAvailability;
  }

  ngOnInit() {
    if (this.availability.find((f) => f === 'never')) {
      setTimeout(() => {
        this.router.navigate([AppConstants.PATH_LOGIN], {
          queryParams: {},
          queryParamsHandling: 'merge',
        });
      }, 1);
      // intentionally not setting ready state or availability state here to avoid flash of error content while redirecting to login
    } else {
      this.processing = false;
      this.setShowStep('details');
      const handleAgreements = () => new Promise<void>((resolve) => {
        if (this.fields.find((f) => f.key === 'agreements')) {
          const challs = this.appStateService.getAppState().getAuthenticationState()
            .getRemainingChallenges()
            .filter((c) => c.authenticationMethod === AppConstants.AC_AM_ACCEPT_AGREEMENTS)
            .map((c) => (c as AcceptAgreementsChallenge).userAgreementChallenges)
          ;
          selectAgreements(challs[0], {locale: this.translate.currentLang || this.translate.defaultLang})
            .then(flattenChallenges)
            .then((agrs) => {
              this.agreements = (agrs as AcceptAgreementChallengeWithReceivedTime[]);
              resolve();
            });
        } else {
          this.agreements = undefined;
          resolve();
        }
      });
      handleAgreements().then(() => {
        const controls = {};
        for (let i = 0; i < this.fields.length; i += 1) {
          const validators = [];
          const f = this.fields[i];
          // ignore the "credentials" field from controls, as actual crendetial types have been populated as e.g. "credentials-password"
          if (f.key !== 'agreements' && !f.key.startsWith('credentials') && f.required) {
            validators.push(Validators.required);
          }
          if (f.key === 'agreements') {
            if (this.agreements && this.agreements.length) {

              this.agreements.forEach((c, ind) => {
                const agreementValidators = [];
                if (c.required) {
                  agreementValidators.push(Validators.requiredTrue);
                }
                controls[f.key + '_' + ind] = new FormControl(false,
                  {validators: agreementValidators, updateOn: 'change'});
              });
            } else {
              validators.push(Validators.requiredTrue);
              controls[f.key] = new FormControl(false,
                {validators, updateOn: 'change'});
            }
          } else if (f.key === 'address-country' && f.required) {
            validators.push(Validators.pattern(AppConstants.NOT_NULL_PATTERN));
            controls['country'] = new FormControl(null, validators);
          } else if (!f.key.startsWith('credentials')) {
            if (f.key === 'email') {
              validators.push(Validators.email);
            }
            controls[f.key.replace('address-', '')] = new FormControl('', validators);
          }
        }
        this.registerForm = new FormGroup(controls);

        this.countries.alphabetical().subscribe(res => {
          this.countryList = res.map((n) => ({label: n.name, value: n.code}));
        });
        this.validateConfirmEmail = this.validateConfirmEmail.bind(this);
        this.registerForm.setValidators([this.validateConfirmEmail]);
        this.subscribeParamsState();
        this.setReady();
      });
    }

  }
  ngOnDestroy() {}


  trimInput(e: any, field: string) {
    const ctrl = this.registerForm.get(field);
    const value = ctrl?.value;
    const trimmed = value?.trim();
    if (value !== trimmed) {
      ctrl?.setValue(trimmed);
    }
  }
  hasAvailabilityRule(rule: string) {
    return !!this.availability.find((f) => f === rule);
  }
  getAgreementNameLink(a: AcceptAgreementChallengeWithReceivedTime) {
    if (a.location) {
      return '<a class="name" href="' + a.location + '" rel="noopener" target="_blank">' + a.name + '</a>';
    } else {
      return '<span class="name">' + a.name + '</span>';
    }
  }
  onInputFocused(event: any) {
    // this is a bit hacky as it relies on the id of the element to match the field it controls, but this is how it currently is
    if (!!(event.target as HTMLElement)?.classList?.contains('is-invalid') && this.hasError((event.target as HTMLElement)?.id)) {
      this.genericError = false;
    }
  }
  isFieldInvalid(field: string): boolean {
    if (field === 'agreements' &&  this.agreements &&  this.agreements.length > 0) {
      const count = this.agreements.length;
      if (count > 0) {
        for (let i = 0; i < count; i += 1) {
          const tmp = this.isFieldInvalid(field + '_' + i);
          if (tmp) {
            return true;
          }
        }
        return false;
      } else {
        return false;
      }
    } else {
      return this.hasError(field) || this.isInputInvalid(field);
    }
  }

  isInputInvalid(field: string): boolean {
    if (field === 'confirmEmail') {
      return (this.registerForm.get(field).invalid
          || (this.registerForm.invalid && this.registerForm.errors && this.registerForm.errors.emailMatch))
        && this.registerForm.get(field).touched;
    } else if (field.startsWith('agreements_')) {
      const siblingsTouched = [...Array(this.agreements ? this.agreements.length : 1).keys()]
        .map((i) => this.registerForm.get('agreements_' + i)?.touched)
        .find((f) => f)
      ;
      return this.registerForm.get(field).invalid && siblingsTouched;
    } else {
      return this.registerForm.get(field).invalid && this.registerForm.get(field).touched;
    }
  }

  isFieldValid(field: string): boolean {
    if (field === 'agreements' && this.agreements &&  this.agreements.length > 0) {
      const count = this.agreements.length;
      if (count > 0) {
        for (let i = 0; i < count; i += 1) {
          const tmp = this.isFieldValid(field + '_' + i);
          if (!tmp) {
            return false;
          }
        }
        return true;
      } else {
        return true;
      }
    } else {
      return !this.hasError(field) && this.isInputValid(field);
    }
  }

  isInputValid(field: string): boolean {
    if (field === 'confirmEmail') {
      return (this.registerForm.get(field).valid
          && (this.registerForm.valid || !this.registerForm.errors || !this.registerForm.errors.emailMatch))
        && this.registerForm.get(field).touched;
    } else if (field.startsWith('agreements_')) {
      const siblingsTouched = [...Array(this.agreements ? this.agreements.length : 1).keys()]
        .map((i) => this.registerForm.get('agreements_' + i)?.touched)
        .find((f) => f)
      ;
      return this.registerForm.get(field).valid && siblingsTouched;
    } else {
      return this.registerForm.get(field).valid && this.registerForm.get(field).touched;
    }
  }

  hasError(field?: string, code?: string): boolean {
    let retValue = false;
    if (field) {
      if (this.errorBody &&
          this.errorBody.error &&
          this.errorBody.childErrors &&
          this.errorBody.childErrors[field]) {
        if (code) {
          if (this.errorBody.childErrors[field].error === code) {
            retValue = true;
          }
        } else {
          retValue = true;
        }
      }
    } else if (this.genericError) {
      retValue = true;
    }
    return retValue;
  }
  resolveFieldValueForIncludedField(field: string): string | boolean | undefined {
    let retVal: string|undefined;
    if (!!field) {
      if (field.startsWith('agreements_') && this.fields.findIndex((val) => val.key === 'agreements') >= 0) {
        retVal = this.registerForm.get(field).value;
      } else if (this.fields.findIndex((val) => val.key === field) >= 0) {
        retVal = this.registerForm.get(field.replace('address-', '')).value;
      }
    }
    return retVal;
  }

  removeUndefined(obj: any): any {
    const keys = Object.keys(obj);
    for (let i = 0; i < keys.length; i += 1) {
      if (obj[keys[i]] === undefined) {
        delete obj[keys[i]];
      }
    }
    return obj;
  }

  onSubmit() {
    this.processingService.show();
    this.processing = true;
    this.errorBody = undefined;
    this.genericError = false;
    this.invitationError = false;
    const creds = this.credentialsComp.getCredentials();
    const address: Address = this.removeUndefined({
      country: this.resolveFieldValueForIncludedField('address-country'),
      streetAddress: this.resolveFieldValueForIncludedField('address-streetAddress'),
      postalCode: this.resolveFieldValueForIncludedField('address-postalCode'),
      locality: this.resolveFieldValueForIncludedField('address-locality'),
      region: this.resolveFieldValueForIncludedField('address-region'),
    });
    const userToRegister: UserForRegisterUser = this.removeUndefined({
      email: this.resolveFieldValueForIncludedField('email'),
      firstName: this.resolveFieldValueForIncludedField('firstName'),
      lastName: this.resolveFieldValueForIncludedField('lastName'),
      phoneNumber: this.resolveFieldValueForIncludedField('phoneNumber'),
      preferredUsername: this.resolveFieldValueForIncludedField('preferredUsername'),
      nickname: this.resolveFieldValueForIncludedField('nickname'),
      professionalTitle: this.resolveFieldValueForIncludedField('professionalTitle'),
      address: address,
    });
    userToRegister.credentials = creds;
    const agr: AgreementAcceptance[]|undefined = this.agreements ? this.agreements.map((c, ind) => {
      return {
          agreementId: c.agreementId,
          accept: this.resolveFieldValueForIncludedField('agreements_' + ind) === true,
          authenticationMethod: AppConstants.AC_AM_ACCEPT_AGREEMENT,
        } as AgreementAcceptance;
      }
    ) : undefined;
    this.logger.debug('Registering...');
    const verificationUri = [];
    if (this.configuration.getProperties().sendEmailValidation && !this.invitationAuthorization) {
      verificationUri.push(LocationUtil.getOwnDomainURL());
      verificationUri.push(getAppBaseHref());
      verificationUri.push(AppConstants.ROUTE_VALIDATE_EMAIL);
      if (verificationUri[0].indexOf('?') < 0) {
        verificationUri.push('?');
      } else {
        verificationUri.push('&');
      }
      verificationUri.push(AppConstants.QP_EMAIL_VALIDATION_KEY);
      verificationUri.push('={0}');

      verificationUri.push('&');
      verificationUri.push(AppConstants.UI_SESSION_ID);
      verificationUri.push('=');
      verificationUri.push(this.appStateService.getAppState().uiSessionId);

      verificationUri.push(
        LocationUtil.validParamsToQueryString(
          '&',
          [AppConstants.QP_EMAIL_VALIDATION_KEY, AppConstants.QP_EMAIL, AppConstants.QP_LOGIN_HINT, AppConstants.UI_SESSION_ID],
          this.configuration.getProperties().allowedOriginsForNextParam)
      );
    }
    this.appStateService.registerUser(
        userToRegister,
        this.invitationAuthorization ,
        verificationUri.length > 0,
        verificationUri.length > 0 ? verificationUri.join('') : undefined,
        undefined,
        true,
      )
      .subscribe(response => {
        this.logger.debug('Registration completed, response %o', response);
        const t: ParamsForRouteNav|string = this.appStateService.getAppState().resolveNextRoute(
          AppConstants.AUHTENTICATION_PROCESS_QUERY_PARAMS)
        ;
        this.processing = false;
        this.processingService.hide();
        if (typeof t === 'string') {
          this.processingService.show();
          window.location.href = t as string;
        } else if (t) {
          this.router.navigate([t.path], {
            queryParams: t.queryParams,
            fragment: t.fragment,
          });
        }

      }, errorResponse => {
        this.logger.debug('Registration error, response %O', errorResponse);
        const challenges = ResponseUtil.readChallenges(errorResponse).required;
        let emailChallenge: AuthenticationChallenge = null;
        let invitationChallenge: AuthenticationChallenge = null;
        if (challenges && challenges.length) {
          emailChallenge = challenges.find(challenge => AppConstants.AC_AM_VERIFY_EMAIL === challenge.authenticationMethod);
          invitationChallenge = challenges
            .find((challenge) => {
              return AppConstants.AC_AM_INVITATION === challenge.authenticationMethod ||
                AppConstants.AC_AM_INVITATION_ACCEPTED === challenge.authenticationMethod;
            })
          ;
        }
        let agreementChallenges: AcceptAgreementsChallenge[] = null;
        if (challenges && challenges.length) {
            agreementChallenges = challenges
              .filter((c) => c.authenticationMethod === AppConstants.AC_AM_ACCEPT_AGREEMENTS)
              .map((c) => c as AcceptAgreementsChallenge)
            ;
        }
        if (agreementChallenges?.length) {
          this.logger.debug('Registration completed');
          const continueToNext = () => {
            const t = this.appStateService.getAppState().resolveNextRoute(
              [AppConstants.QP_EMAIL, AppConstants.QP_LOGIN_HINT]);
            this.processing = false;
            this.processingService.hide();
            if (typeof t === 'string') {
              this.processingService.show();
              window.location.href = t as string;
            } else {
              this.router.navigate([t.path], {
                queryParams: t.queryParams,
                fragment: t.fragment,
              });
            }
          };
          if (!agr) {
            continueToNext();
          } else {
            const errorHandler = (response) => {
              this.consentFailed = true;
              this.logger.debug('Accept failed, response %o', response);
              // These are be theorical changes to agreements mid process and good to go as handled later.
              const challenges2 = ResponseUtil.readChallenges(response).required;
              this.logger.debug('Accept updated challenges: %o', challenges2);
              continueToNext();
            };

            const handler = (response) => {
              this.logger.debug('Accept succeeded, response %o', response);
              continueToNext();
            };
            this.consentFailed = false;
            this.appStateService.addAuthentications(agr, false).subscribe(handler, errorHandler);
          }
        } else if (emailChallenge || invitationChallenge) {
          this.logger.debug('Registration completed');
          // Email challenge and invitation challenges are ok as they are handled in another component
          if (!this.invitationAuthorization && this.configuration.getProperties().sendEmailValidation) {
            this.appStateService.getAppState().addNotification(AppConstants.SN_LOGIN_EMAIL_VALIDATION_SENT);
          }
          const t: ParamsForRouteNav|string = this.appStateService.getAppState().resolveNextRoute(
            [AppConstants.QP_EMAIL, AppConstants.QP_LOGIN_HINT]);
          this.processing = false;
          this.processingService.hide();
          if (typeof t === 'string') {
            this.processingService.show();
            window.location.href = t as string;
          } else if (t) {
            this.router.navigate([t.path], {
              queryParams: {
                ...t.queryParams,
              },
              fragment: t.fragment,
            });
          }
        } else if (errorResponse && errorResponse.error) {
          this.credentialsComp.resetPasskey();
          this.processing = false;
          this.processingService.hide();
          this.errorBody = errorResponse.error;
          this.genericError = true;
          const detailError = this.getDetailsErrors();
          const credError = this.getCredentialErrors();
          if (!!detailError || !credError) {
            this.setShowStep('details');
          }
          if (!detailError &&
            !credError &&
            this.errorBody.error === 'not_authorized' &&
            !this.configuration.getProperties().registrationAvailability.find((f) => f === 'always') &&
            !!this.configuration.getProperties().registrationAvailability.find((f) => f === 'byInvitation')) {
            this.invitationError = true;
          }
        }
      })
    ;
  }

  resolveCountryOptions(obj?: Item): Item[] {
    let t = null;
    if (this.countryList) {
      t = [].concat(this.countryList);
      if (obj) {
        t.unshift(obj);
      }
    }
    return t;
  }

  // noinspection JSMethodCanBeStatic
  resolveFormInputWrapperClass(): string {
    return FormInputComponent.WRAPPER_CLASS + ' checkbox-group';
  }

  // noinspection JSMethodCanBeStatic
  resolveFormInputControlWrapperClass(): string {
    return FormInputComponent.CONTROL_WRAPPER_CLASS;
  }

  private subscribeParamsState() {
    this.route.queryParams.subscribe(params => {
      const keyEmailParams = params[AppConstants.QP_EMAIL];
      const loginHintParams = params[AppConstants.QP_LOGIN_HINT];
      let email;
      if (keyEmailParams) {
        if (Array.isArray(keyEmailParams)) {
          email = keyEmailParams[0];
        } else {
          email = keyEmailParams;
        }
      }
      if (!email) {
        if (loginHintParams) {
          if (Array.isArray(loginHintParams)) {
            email = loginHintParams[0];
          } else {
            email = loginHintParams;
          }
        }
      }
      this.email = email;
      if (this.email) {
        this.registerForm.get('email').setValue(this.email);
      }
      if (params[AppConstants.QP_INVITATION_TOKEN] &&
          params[AppConstants.QP_REQUIRE_CHALLENGE] === AppConstants.AC_AM_INVITATION_ACCEPTED &&
          this.email
      ) {
        if (!this.availability.find((f) => f === 'never') && this.availability.find((f) => f === 'byInvitation')) {
          this.isAvailable = true;
        }
        this.invitationAuthorization = '10DukeInvitation ' + params[AppConstants.QP_INVITATION_TOKEN];
        this.emailAsReadOnly = true;
        if (!!this.registerForm.get('confirmEmail')) {
          this.registerForm.removeControl('confirmEmail');
        }
      } else if (!this.availability.find((f) => f === 'always') &&
        this.availability.find((f) => f === 'byInvitation')) {
        this.isAvailable = false;
      }
    });
  }
  private validateConfirmEmail(control: AbstractControl): ValidationErrors | null {
    const emField = this.registerForm.get('email');
    const cemField = this.registerForm.get('confirmEmail');
    return this.emailAsReadOnly || !cemField || cemField.value === emField.value
      ? null
      : { 'emailMatch': true };
  }
  public credsReady(c) {
    this.onSubmit();
  }
  public resolveSelectCountryLabel(): any {
    return this.translate.instant(_('register.country.select-option'));
  }
  public resolveWindowTitlePart(): Observable<string|undefined> {
    return this.translate.get('register.window-title');
  }
  public showCredentials(): boolean {
    return this.step === 'credentials';
  }
  public showDetails(): boolean {
    return this.step === 'details';
  }
  public showSignInLink(): boolean {
    return this.configuration.getProperties().showRegistrationSignInLink;
  }
  public hasValidCredentials() {
    return !!this.credentialsComp?.getCredentials();
  }
  public setShowStep(stp: string) {
    this.step = stp;
    setTimeout(() => {
      StandaloneComponent.setFocusToElemOrChildControl(this.hostElement, this.focusSelectorPrefix);
    }, 1);
  }
  public getUserName() {
    return this.registerForm.get('email')?.value;
  }
  public getDisplayName() {
    const t = [this.registerForm.get('firstName')?.value, this.registerForm.get('lastName')?.value]
      .filter((f) => !!f);
    if (t.length === 0) {
      return this.registerForm.get('email')?.value;
    } else {
      return t.join(' ');
    }
  }
  public getCredentialErrors() {
    let retVal;
    if (this.errorBody?.childErrors) {
      retVal = {};
      const keys = Object.keys(this.errorBody?.childErrors);
      keys?.filter((f) => {
        return f === 'password' || f === 'confirmPassword';
      }).forEach((k) => {
        retVal[k] = this.errorBody?.childErrors[k];
      });
    }
    return retVal;
  }
  public getDetailsErrors() {
    let retVal;
    if (this.errorBody?.childErrors) {
      retVal = {};
      const keys = Object.keys(this.errorBody?.childErrors);
      keys?.filter((f) => {
        return f !== 'password' && f !== 'confirmPassword';
      }).forEach((k) => {
        retVal[k] = this.errorBody?.childErrors[k];
      });
    }
    return retVal;
  }
}
